import type { SegmentNode } from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useApi } from '../api-context.tsx'
import { listKeys } from './keys.ts'

/**
 * Saved list hooks. Membership is always computed server-side, so nothing here
 * caches members beyond a normal query — a list whose answer is stale is worse
 * than one that takes a moment.
 */

export function useLists() {
  const api = useApi()
  return useQuery({
    queryKey: listKeys.lists(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.lists.get({ query: { limit: 200 } })
      if (error) throw error
      return data.items
    },
  })
}

export type SavedList = NonNullable<ReturnType<typeof useLists>['data']>[number]

export function useList(listId: string) {
  const api = useApi()
  return useQuery({
    queryKey: listKeys.detail(listId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.lists({ id: listId }).get()
      if (error) throw error
      return data
    },
  })
}

export function useListMembers(listId: string) {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: listKeys.members(listId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1
        .lists({ id: listId })
        .members.get({ query: pageParam ? { cursor: pageParam } : {} })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })

  return {
    members: query.data?.pages.flatMap((p) => p.items) ?? [],
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isPending,
  }
}

/**
 * Live count for a tree being edited. Debounced, because the builder fires on
 * every keystroke and each preview is a real query over the whole org.
 */
export function useSegmentPreview(rules: SegmentNode, debounceMs = 400) {
  const api = useApi()
  const [settled, setSettled] = useState(rules)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(rules), debounceMs)
    return () => clearTimeout(timer)
  }, [rules, debounceMs])

  const query = useQuery({
    queryKey: listKeys.preview(settled),
    queryFn: async () => {
      const { data, error } = await api.api.v1.lists.preview.post({ rules: settled })
      if (error) throw error
      return data
    },
  })

  return {
    count: query.data?.count ?? null,
    summary: query.data?.summary ?? '',
    isLoading: query.isFetching,
    error: query.error,
    /** True while the debounce has not caught up with the latest edit. */
    isStale: settled !== rules,
  }
}

function useListInvalidation() {
  const queryClient = useQueryClient()
  return (data?: { id: string }) => {
    if (data) {
      void queryClient.invalidateQueries({ queryKey: listKeys.detail(data.id) })
      void queryClient.invalidateQueries({ queryKey: listKeys.members(data.id) })
    }
    void queryClient.invalidateQueries({ queryKey: listKeys.lists() })
  }
}

export function useCreateList() {
  const api = useApi()
  const invalidate = useListInvalidation()
  return useMutation({
    mutationFn: async (body: { name: string; description?: string | null; rules: SegmentNode }) => {
      const { data, error } = await api.api.v1.lists.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data),
  })
}

export function useUpdateList(listId: string) {
  const api = useApi()
  const invalidate = useListInvalidation()
  return useMutation({
    mutationFn: async (body: {
      name?: string
      description?: string | null
      rules?: SegmentNode
    }) => {
      const { data, error } = await api.api.v1.lists({ id: listId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data),
  })
}

export function useDeleteList() {
  const api = useApi()
  const invalidate = useListInvalidation()
  return useMutation({
    mutationFn: async (listId: string) => {
      const { error } = await api.api.v1.lists({ id: listId }).delete()
      if (error) throw error
    },
    onSuccess: () => invalidate(),
  })
}

export function useRefreshList() {
  const api = useApi()
  const invalidate = useListInvalidation()
  return useMutation({
    mutationFn: async (listId: string) => {
      const { data, error } = await api.api.v1.lists({ id: listId }).refresh.post()
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data),
  })
}
